import { DuplicatePlotDefinitionError, InvalidPlotFeatureError, PlotDefinitionNotFoundError, } from "./errors.js";
import { createPlotFeature } from "./types.js";
export class PlotRegistry {
    #definitions = new Map();
    register(definition) {
        if (this.#definitions.has(definition.type)) {
            throw new DuplicatePlotDefinitionError(definition.type);
        }
        this.#assertDefinition(definition);
        this.#definitions.set(definition.type, definition);
        return this;
    }
    registerMany(definitions) {
        for (const definition of definitions) {
            this.register(definition);
        }
        return this;
    }
    replace(definition) {
        this.#assertDefinition(definition);
        this.#definitions.set(definition.type, definition);
        return this;
    }
    unregister(type) {
        return this.#definitions.delete(type);
    }
    has(type) {
        return this.#definitions.has(type);
    }
    get(type) {
        const definition = this.#definitions.get(type);
        if (!definition) {
            throw new PlotDefinitionNotFoundError(type);
        }
        return definition;
    }
    list() {
        return [...this.#definitions.values()].sort((left, right) => left.type.localeCompare(right.type));
    }
    canonicalize(feature) {
        const definition = this.get(feature.plotType);
        const controlPoints = definition.canonicalizeControlPoints?.({ feature });
        if (!controlPoints)
            return feature;
        assertControlPointPermutation(feature.controlPoints, controlPoints);
        return createPlotFeature({ ...feature, controlPoints });
    }
    validate(feature) {
        const issues = [];
        let canonical = feature;
        try {
            canonical = this.canonicalize(feature);
        }
        catch (error) {
            issues.push({
                code: "INVALID_CONTROL_POINT_CANONICALIZATION",
                message: error instanceof Error
                    ? error.message
                    : "Control-point canonicalization failed.",
                severity: "error",
            });
            return { valid: false, issues };
        }
        const definition = this.get(canonical.plotType);
        const pointCount = canonical.controlPoints.length;
        if (pointCount < definition.controlSchema.minPoints) {
            issues.push({
                code: "TOO_FEW_CONTROL_POINTS",
                message: `${definition.type} requires at least ${definition.controlSchema.minPoints} control points; received ${pointCount}.`,
                severity: "error",
            });
        }
        if (pointCount > definition.controlSchema.maxPoints) {
            issues.push({
                code: "TOO_MANY_CONTROL_POINTS",
                message: `${definition.type} accepts at most ${definition.controlSchema.maxPoints} control points; received ${pointCount}.`,
                severity: "error",
            });
        }
        for (const [index, point] of canonical.controlPoints.entries()) {
            const [longitude, latitude] = point;
            if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
                issues.push({
                    code: "NON_FINITE_CONTROL_POINT",
                    message: `Control point ${index} contains a non-finite coordinate.`,
                    severity: "error",
                });
            }
            if (latitude < -90 || latitude > 90) {
                issues.push({
                    code: "LATITUDE_OUT_OF_RANGE",
                    message: `Control point ${index} has latitude ${latitude}, outside [-90, 90].`,
                    severity: "error",
                });
            }
        }
        const custom = definition.validate?.({ feature: canonical });
        if (custom) {
            issues.push(...custom.issues);
        }
        return {
            valid: !issues.some((issue) => issue.severity === "error"),
            issues,
        };
    }
    assertValid(feature) {
        const validation = this.validate(feature);
        if (!validation.valid) {
            throw new InvalidPlotFeatureError(validation.issues.map((issue) => issue.message).join(" "));
        }
    }
    generate(feature) {
        const canonical = this.canonicalize(feature);
        this.assertValid(canonical);
        return this.get(canonical.plotType).generate({ feature: canonical });
    }
    #assertDefinition(definition) {
        if (!definition.type.trim()) {
            throw new InvalidPlotFeatureError("Plot definition type must not be empty.");
        }
        if (!Number.isInteger(definition.controlSchema.minPoints) ||
            definition.controlSchema.minPoints < 0) {
            throw new InvalidPlotFeatureError("controlSchema.minPoints must be a non-negative integer.");
        }
        if (!Number.isFinite(definition.controlSchema.maxPoints) &&
            definition.controlSchema.maxPoints !== Number.POSITIVE_INFINITY) {
            throw new InvalidPlotFeatureError("controlSchema.maxPoints must be finite or Infinity.");
        }
        if (definition.controlSchema.maxPoints < definition.controlSchema.minPoints) {
            throw new InvalidPlotFeatureError("controlSchema.maxPoints must be >= minPoints.");
        }
    }
}
function assertControlPointPermutation(original, canonical) {
    if (canonical.length !== original.length) {
        throw new InvalidPlotFeatureError("Control-point canonicalization must preserve the control count.");
    }
    const used = new Array(original.length).fill(false);
    for (const point of canonical) {
        const match = original.findIndex((candidate, index) => !used[index] &&
            candidate[0] === point[0] &&
            candidate[1] === point[1]);
        if (match < 0) {
            throw new InvalidPlotFeatureError("Control-point canonicalization must only reorder existing coordinates.");
        }
        used[match] = true;
    }
}
//# sourceMappingURL=registry.js.map